import { useTeams } from '@/hooks/useTeams';
import { supabase } from '@/lib/supabase';
import { callApi } from '@/utils/api';
import { formatFilename } from '@/utils/campaigns';
import {
  getCampaignQueryKey,
  getCampaignsQueryKey,
  getDownloadCampaignQueryKey,
  getRecommendedKpiQueryKey,
} from '@/utils/query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

export enum KPI {
  CONVERSIONS = 'CONVERSIONS',
  INTEREST = 'INTEREST',
  CONSIDERATION = 'CONSIDERATION',
  AWARENESS = 'AWARENESS',
}

export enum Format {
  VIDEO = 'VIDEO',
  CAROUSEL_ALBUM = 'CAROUSEL_ALBUM',
  IMAGE = 'IMAGE',
}

export enum CampaignStatus {
  DRAFT = 'DRAFT',
  GENERATING = 'GENERATING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export type Post = {
  id: string;
  teamId: string;
  campaignId: string;
  dateTime: string;
  format: Format;
  theme?: string;
  caption: string;
  createdAt: string;
  updatedAt: string;

  // Video specific fields
  hook?: string;
  musicStyle?: string;
  talkingPoints?: string[];
  brollShots?: string[];
  overlayText?: string;

  // Photo specific fields
  visualStyle?: string;
  photoCopy?: string;

  // Carousel specific fields
  slideCount?: number;
  firstSlide?: string;
  slideFlow?: string[];
  callToAction?: string;
};

export type KeyDate = {
  date: Date;
  context: string;
};

export type Campaign = {
  id: string;
  team_id: string;
  name: string;
  key_dates?: KeyDate[];
  objectives: string[];
  kpi: KPI;
  start_date: Date;
  end_date: Date;
  created_at: string;
  content_month: string | null;
  status: CampaignStatus;
  posts: Post[];
};

export type CreateCampaignInput = Pick<
  Campaign,
  'name' | 'key_dates' | 'start_date' | 'end_date' | 'objectives' | 'kpi'
>;

export type RecommendedKpi = {
  id: string;
  created_at: string;
  justification: string;
  kpi: KPI;
  tactic: string;
  team_id: string;
};

export const useCampaigns = () => {
  const queryClient = useQueryClient();
  const { activeTeam } = useTeams();

  const query = useQuery<Campaign[]>({
    queryKey: getCampaignsQueryKey(),
    queryFn: async () => {
      const { data: campaigns, error } = await callApi(
        supabase,
        '/campaigns/all',
        {
          method: 'GET',
        },
      );

      if (error) return [];
      if (!campaigns) return [];

      return campaigns;
    },
    enabled: !!activeTeam?.data?.tokens,
  });

  const createCampaign = useMutation({
    mutationFn: async (data: CreateCampaignInput): Promise<Campaign> => {
      const { data: newCampaign, error } = await callApi(
        supabase,
        '/campaigns',
        {
          method: 'POST',
          data: {
            ...data,
            start_date: data.start_date?.toISOString(),
            end_date: data.end_date?.toISOString(),
          },
        },
      );

      if (error) throw error;
      if (!newCampaign) throw new Error('Failed to create campaign');

      return newCampaign;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getCampaignsQueryKey() });
    },
  });

  const recommendedKpi = useQuery<RecommendedKpi>({
    queryKey: getRecommendedKpiQueryKey(),
    queryFn: async () => {
      const { data, error } = await callApi(
        supabase,
        '/campaigns/recommendations/kpi',
        {
          method: 'GET',
        },
      );

      if (error) throw error;

      return data;
    },
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !!activeTeam?.data?.tokens,
  });

  return {
    ...query,
    createCampaign,
    recommendedKpi,
  };
};

export const useCampaign = (campaignId?: string) => {
  const queryClient = useQueryClient();
  const previousStatusRef = useRef<CampaignStatus | null>(null);

  return useQuery<Campaign>({
    queryKey: getCampaignQueryKey(campaignId),
    queryFn: async () => {
      const { data: campaign, error } = await callApi(
        supabase,
        `/campaigns/${campaignId}`,
        {
          method: 'GET',
        },
      );

      if (error) throw error;
      if (!campaign) throw new Error('Campaign not found');

      if (
        previousStatusRef.current === CampaignStatus.GENERATING &&
        campaign.status !== CampaignStatus.GENERATING
      ) {
        queryClient.invalidateQueries({ queryKey: getCampaignsQueryKey() });
        queryClient.invalidateQueries({
          queryKey: getCampaignQueryKey(campaignId),
        });
      }

      previousStatusRef.current = campaign.status;

      return campaign as Campaign;
    },
    enabled: !!campaignId,
    refetchInterval: (query) =>
      query.state.data?.status === CampaignStatus.GENERATING ? 30000 : false,
  });
};

export const useDownloadCampaign = (
  campaignId?: string,
  campaignName?: string,
) => {
  return useQuery({
    queryKey: getDownloadCampaignQueryKey(campaignId),
    queryFn: async () => {
      const { data, error } = await callApi(
        supabase,
        `/campaigns/download/${campaignId || ''}`,
        {
          method: 'GET',
          responseType: 'blob',
        },
      );

      if (error) throw error;

      // Format date for filename
      const date = new Date().toISOString().split('T')[0];
      const formattedName = formatFilename(campaignName);
      const filename = campaignId
        ? `${formattedName}-${date}.csv`
        : `all-campaigns-${date}.csv`;

      // Create and trigger download
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return data;
    },
    enabled: false,
    retry: false,
  });
};

export const useDeleteCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (campaignId: string) => {
      const { error } = await callApi(supabase, `/campaigns/${campaignId}`, {
        method: 'DELETE',
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getCampaignsQueryKey() });
    },
  });
};
